import { Alert, Badge, Box, Button, Card, Group, Select, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { parseMutationError } from '../api/errors';
import { joinFromColumns, splitForColumns, type LocalizedString } from '../api/localized-string';
import { useProduct, useUpdateProduct } from '../api/products';
import { LocalizedStringInput } from '../components/localized-string-input';

export function ProductEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const productQuery = useProduct(id);
  const update = useUpdateProduct(id);

  const [displayName, setDisplayName] = useState<LocalizedString>({ default: '' });
  const [status, setStatus] = useState('active');
  const [error, setError] = useState<string | null>(null);

  const product = productQuery.data;

  useEffect(() => {
    if (!product) return;
    setDisplayName(joinFromColumns(product.displayName, product.displayNameTranslations ?? null));
    setStatus(product.status);
  }, [product?.id]);

  if (productQuery.isPending) return <Text p="md">Loading…</Text>;
  if (productQuery.isError || !product) return <Text p="md">Could not load product.</Text>;

  const retired = !!product.deletedAt;
  const split = splitForColumns(displayName);
  // Cheap deep-equal-by-JSON for the translations comparison — the
  // map only carries short string values, both sides have stable
  // key order, and we just need a dirty-button heuristic.
  const translationsChanged =
    JSON.stringify(product.displayNameTranslations ?? null) !==
    JSON.stringify(split.translations ?? null);
  const dirty =
    !retired &&
    (product.displayName !== split.default.trim() ||
      product.status !== status ||
      translationsChanged);

  function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    update.mutate(
      {
        display_name: split.default.trim(),
        display_name_translations: split.translations,
        status,
      },
      {
        onSuccess: () => {
          notifications.show({ color: 'green', message: 'Product updated.' });
          navigate(`/products/${id}`);
        },
        onError: (err) => setError(parseMutationError(err, 'Could not save changes.')),
      },
    );
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Group>
          <Title order={2}>{product.code}</Title>
          {retired && (
            <Badge color="gray" variant="light">
              retired
            </Badge>
          )}
        </Group>
        <Button variant="subtle" onClick={() => navigate(`/products/${id}`)}>
          Back to product
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Card withBorder padding="lg" maw={680}>
        <Box component="form" onSubmit={handleSave}>
          <Stack gap="md">
            <Title order={5}>Profile</Title>
            <LocalizedStringInput
              label="Display name"
              required
              value={displayName}
              onChange={setDisplayName}
              disabled={update.isPending || retired}
            />
            <Select
              label="Status"
              value={status}
              onChange={(v) => setStatus(v ?? 'active')}
              data={[
                { value: 'active', label: 'Active' },
                { value: 'archived', label: 'Archived' },
              ]}
              disabled={update.isPending || retired}
            />
            <Group justify="flex-end">
              <Button type="submit" loading={update.isPending} disabled={!dirty}>
                Save changes
              </Button>
            </Group>
          </Stack>
        </Box>
      </Card>
    </Stack>
  );
}
